import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, NavigateOptions } from "react-router-dom";

export const fetchCurrentAssetHash = async (): Promise<string | undefined> => {
  return await fetch("/hash.json")
    .then((r) => r.json())
    .then((json) => json.hash);
};

const useCurrentAssetHash = () => {
  return useQuery({
    queryKey: ["asset-hash"],
    queryFn: async () => await fetchCurrentAssetHash(),
    refetchInterval: 10 * 60 * 1_000, // Re-fetch every 10 minutes
  });
};

const UpdateContext = createContext<{ updateNeeded: boolean }>({
  updateNeeded: false,
});

export const UpdateContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const currentHash = useCurrentAssetHash().data;
  const [pageLoadHash, setPageLoadHash] = useState<string | undefined>(
    undefined
  );
  useEffect(() => {
    if (!pageLoadHash && currentHash) {
      setPageLoadHash(currentHash);
    }
  }, [currentHash]);

  const updateNeeded = Boolean(
    currentHash && pageLoadHash && currentHash !== pageLoadHash
  );

  return (
    <UpdateContext.Provider value={{ updateNeeded }}>
      {children}
    </UpdateContext.Provider>
  );
};

export const useNavigateWithReloadCheck = (): ((
  to: string,
  opts?: NavigateOptions
) => void) => {
  const navigate = useNavigate();
  const { updateNeeded } = useContext(UpdateContext);

  const wrapped = (to: string, opts?: NavigateOptions) => {
    if (updateNeeded) {
      // NB: This query parameter is also referenced in index.html
      window.location.href = `${
        window.location.origin
      }/?updated_at=${Date.now()}#${to}`;
    } else {
      navigate(to, opts);
    }
  };
  return wrapped;
};
